/**
 * useNotificationStream — opens an SSE connection to the backend and
 * invalidates the React Query notifications cache on each `notification`
 * event so the bell badge and dropdown refresh without polling.
 *
 * Architecture: see migrofast docs/activity-center/ADR_AC_001 (SSE vs WS)
 * and ciclo_04_UI_frontend_spec_2026-04-23.md.
 *
 * Usage:
 *   function CRMLayout() {
 *     useNotificationStream();  // mount once per session
 *     return <Outlet />;
 *   }
 *
 * The browser's built-in EventSource auto-reconnects on transient network
 * failures (with Last-Event-Id). Authentication uses the existing session
 * cookie via `withCredentials: true`.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notificationKeys } from './useActivityCenter';

function getStreamUrl(): string {
  const base =
    (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env
      ?.VITE_API_BASE_URL ?? '/api';
  // Ensure exactly one slash between base and path
  const trimmed = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${trimmed}/notifications/stream`;
}

/**
 * Audit mode disables the persistent SSE so the page can reach `document_idle`,
 * which Claude-in-Chrome / Playwright need in order to screenshot/click.
 * Triggered by `?audit=1` (ad-hoc browser audits) or
 * `VITE_DISABLE_NOTIFICATION_STREAM=true` (audit/e2e builds). No effect for real users.
 */
function isAuditMode(): boolean {
  const env = (import.meta as unknown as { env?: { VITE_DISABLE_NOTIFICATION_STREAM?: string } })
    .env;
  if (env?.VITE_DISABLE_NOTIFICATION_STREAM === 'true') return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('audit');
  } catch {
    return false;
  }
}

export function useNotificationStream(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof EventSource === 'undefined' || isAuditMode()) {
      // SSR / unsupported, or audit mode (the page must reach document_idle):
      // fall back to the polling already in place.
      return;
    }

    const es = new EventSource(getStreamUrl(), { withCredentials: true });

    const onNotification = () => {
      // A new notification arrived for this user → invalidate the whole
      // notifications tree (list + unread-count). We deliberately don't
      // try to merge the payload into the cache because the list query
      // has filters/paging; a refetch is simpler and always correct.
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    };

    // Keep-alive pings from the server: no-op, just keep the connection warm.
    const onPing = () => {};

    es.addEventListener('notification', onNotification);
    es.addEventListener('ping', onPing);

    return () => {
      es.removeEventListener('notification', onNotification);
      es.removeEventListener('ping', onPing);
      es.close();
    };
    // queryClient is stable (QueryClientProvider-scoped); mount once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
